import { useState, useEffect, useCallback, useMemo } from "react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import { useCurrentRentalPoint } from "@/stores/useCurrentRentalPoint";
import { useClientData } from "@/domains/clients/hooks/useClientData";
import { useLocationsUnified } from "@/hooks/useLocationsUnified";
import { useInventoryUnified } from '@/hooks/useInventoryUnified';
import { useStoreProductsUnified } from '@/hooks/store/useStoreProductsUnified';
import { useRentalKitsUnified } from "@/hooks/rentals/useRentalKitsUnified";
import { useOrderCalculations } from "./useOrderCalculations";
import { OrderFormData, LastAction } from "./types";

// Выносим initialFormData за пределы компонента, чтобы избежать пересоздания объекта
const initialFormData: OrderFormData = {
  clientId: '',
  rentalPointId: '',
  returnPointId: '',
  showReturnPoint: false,
  startDate: null,
  endDate: null,
  status: 'active',
  paymentStatus: 'unpaid',
  paymentMethodId: '',
  notes: '',
  discount: 0,
  depositAmount: 0,
  promoCode: '',
  totalAmount: 0,
  tariffId: '',
  inventoryItems: [],
  signatureId: '',
  isStorePurchase: false,
  selectedKits: [],
  generateContract: false,
  deliveryAddress: ''
};

type AddOrderDraft = {
  userId?: string;
  clientId: string;
  rentalPointId: string;
  returnPointId?: string;
  startDate: Date | null;
  endDate: Date | null;
  status: string;
  paymentStatus?: string;
  paymentMethodId?: string;
  tariffId?: string;
  notes?: string;
  discount: number;
  depositAmount: number;
  promoCode?: string;
  inventoryItems: Array<{ id: string; quantity: number }>;
  storeItems: Array<{ id: string; quantity: number }>;
  isStorePurchase?: boolean;
  selectedKits: string[];
  totalAmount: number;
  deliveryAddress?: string;
};

interface UseAddOrderDialogProps {
  onSave?: (data: AddOrderDraft) => Promise<void> | void;
  onSuccess?: () => void;
  onClose: () => void;
  preselectedClientId?: string;
}

export function useAddOrderDialog({ onSave, onSuccess, onClose, preselectedClientId }: UseAddOrderDialogProps) {
  // Stable hooks order - all hooks called unconditionally at top level
  const { toast } = useToast();
  const { user } = useAuth();
  const { currentRentalPoint } = useCurrentRentalPoint();
  const { clients, isLoading: clientsLoading } = useClientData();
  const { locations } = useLocationsUnified();
  const { kits: rentalKits, isLoading: kitsLoading } = useRentalKitsUnified();
  
  // Initial form state уже определен выше

  // Form state
  const [formData, setFormData] = useState<OrderFormData>(initialFormData);

  // Other state
  const [selectedKits, setSelectedKits] = useState<string[]>([]);
  const [selectedStoreItems, setSelectedStoreItems] = useState<Array<{ id: string; quantity: number }>>([]);
  const [lastAction, setLastAction] = useState<LastAction | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Determine active rental point ID
  const activeRentalPointId = formData.rentalPointId || currentRentalPoint?.id || locations[0]?.id || '';

  // Data hooks - both inventory and store items filtered by active rental point
  const { inventory: inventoryItems, loading: inventoryLoading } = useInventoryUnified({ rentalPointId: activeRentalPointId });

  // Explicit inventory loading control to prevent perpetual spinners
  const [explicitInventoryLoading, setExplicitInventoryLoading] = useState<boolean>(true);
  useEffect(() => {
    // If user is not available, don't block UI
    if (!user) {
      setExplicitInventoryLoading(false);
      return;
    }

    // When underlying hook reports not loading, reflect it immediately
    if (!inventoryLoading) {
      setExplicitInventoryLoading(false);
      return;
    }

    // Otherwise, we're in loading state. Add a safety timeout to avoid infinite spinner
    setExplicitInventoryLoading(true);
    const timeoutId = setTimeout(() => {
      // Safety switch-off to avoid hanging UI (e.g., network/sdk hiccups)
      setExplicitInventoryLoading(false);
      console.warn('[useAddOrderDialog] Inventory loading exceeded timeout, disabling spinner fallback');
    }, 7000);

    return () => clearTimeout(timeoutId);
  }, [user, inventoryLoading]);
  // Исправление: не фильтруем товары магазина по пункту проката, если activeRentalPointId пустой
  // Заворачиваем в useMemo, чтобы зависимость была стабильной
  const storeFilters = useMemo(() => (
    activeRentalPointId ? { rentalPointId: activeRentalPointId } : {}
  ), [activeRentalPointId]);
  const { products: availableStoreItems, isLoading: storeLoading } = useStoreProductsUnified(storeFilters);

  // Calculate total amount
  const { totalAmount } = useOrderCalculations({
    formData,
    selectedStoreItems,
    selectedKits,
    discount: formData.discount || 0,
    inventoryItems,
    storeItems: availableStoreItems,
    kits: rentalKits
  });

  // Initialize rental point ID
  useEffect(() => {
    if (!formData.rentalPointId && activeRentalPointId) {
      console.log('[useAddOrderDialog] Initializing rental point ID:', activeRentalPointId);
      setFormData(prev => ({ ...prev, rentalPointId: activeRentalPointId }));
    }
  }, [activeRentalPointId, formData.rentalPointId]);

  // Clear selected items when rental point changes (to avoid cross-location inventory issues)
  useEffect(() => {
    if (formData.rentalPointId && formData.inventoryItems.length > 0) {
      console.log('[useAddOrderDialog] Rental point changed, clearing selected inventory items');
      setFormData(prev => ({ ...prev, inventoryItems: [] }));
      setSelectedStoreItems([]);
      setSelectedKits([]);
      setLastAction(null);
    }
  }, [formData.rentalPointId]); // Only depend on rentalPointId to avoid infinite loops

  // Reset form state when dialog closes
  const resetFormState = useCallback(() => {
    console.log('[useAddOrderDialog] Resetting form state');
    setFormData(initialFormData);
    setSelectedKits([]);
    setSelectedStoreItems([]);
    setLastAction(null);
    setIsSubmitting(false);
    setValidationErrors([]);
  }, []); // Убираем initialFormData из зависимостей, так как он теперь стабильный

  // Initialize preselected client
  useEffect(() => {
    if (preselectedClientId && !formData.clientId) {
      console.log('[useAddOrderDialog] Setting preselected client ID:', preselectedClientId);
      setFormData(prev => ({ ...prev, clientId: preselectedClientId }));
    }
  }, [preselectedClientId, formData.clientId]);

  // Form field change handler (generic, strictly typed by field key)
  const handleFieldChange = useCallback(<K extends keyof OrderFormData>(field: K, value: OrderFormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Inventory item change handler
  const handleInventoryItemChange = useCallback((id: string, quantity: number) => {
    setFormData(prev => {
      const currentItems = [...prev.inventoryItems];
      const existingIndex = currentItems.findIndex(item => item.id === id);
      
      if (quantity > 0) {
        if (existingIndex >= 0) {
          currentItems[existingIndex] = { id, quantity };
        } else {
          currentItems.push({ id, quantity });
        }
      } else {
        if (existingIndex >= 0) {
          currentItems.splice(existingIndex, 1);
        }
      }
      
      // Автоматически устанавливаем даты аренды, если их нет (для корректного расчета)
      const updatedFormData = { ...prev, inventoryItems: currentItems };
      
      // CRITICAL FIX: Always set rental dates when inventory items are selected
      if (currentItems.length > 0) {
        // Если даты не установлены или установлены некорректно, устанавливаем дефолтные
        if (!prev.startDate || !prev.endDate || prev.endDate <= prev.startDate) {
          const now = new Date();
          const tomorrow = new Date(now);
          tomorrow.setDate(tomorrow.getDate() + 1);
          
          updatedFormData.startDate = now;
          updatedFormData.endDate = tomorrow;
        }
      }
      
      return updatedFormData;
    });

    // Update last action
    const inventoryItem = inventoryItems.find(item => item.id === id);
    if (inventoryItem) {
      setLastAction({
        type: 'inventory',
        item: { id, quantity }
      });
    }
  }, [inventoryItems]);

  // Store item change handler
  const handleStoreItemChange = useCallback((id: string, quantity: number) => {
    setSelectedStoreItems(prev => {
      const currentItems = [...prev];
      const existingIndex = currentItems.findIndex(item => item.id === id);
      
      if (quantity > 0) {
        if (existingIndex >= 0) {
          currentItems[existingIndex] = { id, quantity };
        } else {
          currentItems.push({ id, quantity });
        }
      } else {
        if (existingIndex >= 0) {
          currentItems.splice(existingIndex, 1);
        }
      }
      
      return currentItems;
    });

    // Update last action
    const storeItem = availableStoreItems.find(item => item.id === id);
    if (storeItem) {
      setLastAction({
        type: 'store_item',
        item: { id, quantity, name: storeItem.name, price: storeItem.price }
      });
    }
  }, [availableStoreItems]);


  // Undo last action
  const handleUndoLastAction = useCallback(() => {
    if (!lastAction) return;

    if (lastAction.type === 'inventory') {
      handleInventoryItemChange(lastAction.item.id, 0);
    } else if (lastAction.type === 'store_item') {
      handleStoreItemChange(lastAction.item.id, 0);
    }

    setLastAction(null);
  }, [lastAction, handleInventoryItemChange, handleStoreItemChange]);

  // Validation
  const validateForm = useCallback((): string[] => {
    const errors: string[] = [];

    if (!formData.clientId) errors.push('Выберите клиента');
    if (!formData.rentalPointId) errors.push('Выберите пункт проката');
    if (!formData.startDate) errors.push('Выберите дату начала');
    if (!formData.endDate) errors.push('Выберите дату окончания');
    if (!formData.tariffId) errors.push('Выберите тариф');

    // Check if at least one item is selected
    const hasItems = formData.inventoryItems.length > 0 || selectedStoreItems.length > 0 || selectedKits.length > 0;
    if (!hasItems) errors.push('Добавьте хотя бы один товар или услугу');

    return errors;
  }, [formData, selectedStoreItems, selectedKits]);

  // Form submission
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    setValidationErrors([]);

    const errors = validateForm();
    if (errors.length > 0) {
      setValidationErrors(errors);
      toast({
        title: "Ошибка валидации",
        description: errors.join(", "),
        variant: "destructive"
      });
      return;
    }

    // Use formData.rentalPointId as the primary source, fallback to activeRentalPointId
    const finalRentalPointId = formData.rentalPointId || activeRentalPointId;
    if (!finalRentalPointId) {
      toast({
        title: "Ошибка",
        description: "Не выбран пункт проката",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const orderData = {
        userId: user?.id,
        clientId: formData.clientId,
        rentalPointId: finalRentalPointId,
        returnPointId: formData.returnPointId,
        startDate: formData.startDate,
        endDate: formData.endDate,
        status: formData.status,
        paymentStatus: formData.paymentStatus,
        paymentMethodId: formData.paymentMethodId,
        tariffId: formData.tariffId,
        notes: formData.notes,
        discount: formData.discount || 0,
        depositAmount: formData.depositAmount || 0,
        promoCode: formData.promoCode,
        inventoryItems: formData.inventoryItems,
        storeItems: selectedStoreItems,
        isStorePurchase: formData.isStorePurchase,
        selectedKits,
        totalAmount,
        deliveryAddress: formData.deliveryAddress
      };

      if (onSave) {
        await onSave(orderData);
      }

      if (onSuccess) {
        onSuccess();
      }

      // Сбрасываем состояние формы перед закрытием
      resetFormState();
      onClose();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Не удалось создать заказ';
      toast({
        title: "Ошибка",
        description: message,
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, selectedKits, selectedStoreItems, totalAmount, activeRentalPointId, validateForm, onSave, onSuccess, onClose, toast, user?.id, resetFormState]);

  return {
    formData,
    selectedKits,
    setSelectedKits,
    selectedStoreItems,
    setSelectedStoreItems,
    lastAction,
    isStorePurchase: formData.isStorePurchase || false,
    // Expose resolved active rental point ID for reliable submissions
    activeRentalPointId,
    handleFieldChange,
    handleInventoryItemChange,
    handleStoreItemChange,
    handleUndoLastAction,
    handleSubmit,
    isSubmitting,
    validationErrors,
    clientsData: clients,
    clientsLoading,
    locations,
    currentRentalPoint,
    totalAmount,
    inventoryItems,
    inventoryLoading: explicitInventoryLoading,
    storeItems: availableStoreItems,
    storeLoading,
    rentalKits,
    kitsLoading,
    resetFormState
  };
}
